import styled, { keyframes, createGlobalStyle } from "styled-components";

// ── Animations ────────────────────────────────────────────────────────────────
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.5; }
`;

export const scanline = keyframes`
  0%   { transform: translateY(-100%); }
  100% { transform: translateY(100vh); }
`;

// ── Global ────────────────────────────────────────────────────────────────────
export const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Space+Grotesk:wght@400;700;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
`;

// ── Layout ────────────────────────────────────────────────────────────────────
export const MainContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  min-height: 100vh;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow: hidden;
  color: var(--text-primary);
`;

export const SideContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  height: 100vh;
  position: relative;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: var(--accent-soft); }
  &::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 4px; opacity: 0.4; }
`;

export const BackgroundPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.07;
  background-image:
    radial-gradient(circle at 25% 25%, var(--accent-secondary) 2px, transparent 2px),
    radial-gradient(circle at 75% 75%, var(--accent) 1px, transparent 1px);
  background-size: 100px 100px, 60px 60px;
  pointer-events: none;
`;

export const ScanlineEffect = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 100;
  overflow: hidden;
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    background: linear-gradient(transparent, var(--accent-secondary-soft), transparent);
    animation: ${scanline} 8s linear infinite;
  }
`;

export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 2.5rem 2rem;
`;

// ── Header ────────────────────────────────────────────────────────────────────
export const HeaderSection = styled.header`
  text-align: center;
  margin-bottom: 3.5rem;
  animation: ${fadeInUp} 0.6s ease-out both;
`;

export const Title = styled.h1`
  font-size: 3rem;
  font-weight: 900;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
  @media (max-width: 768px) { font-size: 2rem; }
`;

export const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--text-muted);
  margin-top: 0.5rem;
  font-family: 'JetBrains Mono', monospace;
  letter-spacing: 0.05em;
`;

// ── Menu Grid ─────────────────────────────────────────────────────────────────
export const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const ActionButton = styled.button`
  position: relative;
  background: var(--surface-glass);
  backdrop-filter: blur(20px);
  border: 2px solid var(--border-subtle);
  border-radius: 20px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 160px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, var(--accent-secondary-soft) 0%, var(--accent-soft) 100%);
    opacity: 0;
    transition: opacity 0.35s ease;
    border-radius: 18px;
  }
  &:hover::before { opacity: 1; }
  &:hover {
    transform: translateY(-6px);
    border-color: var(--border-strong);
    box-shadow: var(--shadow-lg);
  }
  &:active { transform: translateY(-2px); }
  > * { position: relative; z-index: 2; }
`;

export const ActiveButton = styled(ActionButton)`
  border-color: var(--border-strong);
  background: var(--accent-soft);
`;

export const ButtonIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.75rem;
  filter: drop-shadow(0 2px 6px var(--accent-soft));
`;

export const ButtonText = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-heading);
  margin: 0 0 0.4rem;
`;

export const ButtonDescription = styled.p`
  font-size: 0.8rem;
  color: var(--text-muted);
  line-height: 1.4;
  transition: color 0.3s;
  ${ActionButton}:hover & { color: var(--text-secondary); }
`;

// ── Panels ────────────────────────────────────────────────────────────────────
export const Panel = styled.div`
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  border-radius: 20px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
`;

export const PanelTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-heading);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'JetBrains Mono', monospace;
  letter-spacing: -0.01em;
`;

export const EditorArea = styled.textarea`
  width: 100%;
  height: 420px;
  background: var(--surface-overlay);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  padding: 1.25rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.82rem;
  color: var(--text-heading);
  line-height: 1.7;
  resize: none;
  outline: none;
  transition: border-color 0.3s;
  &:focus { border-color: var(--border-strong); background: var(--input-bg-focus); }
`;

// ── Workflow ──────────────────────────────────────────────────────────────────
export const WorkflowButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

export const WorkflowBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.25s ease;
  opacity: ${p => p.disabled ? 0.45 : 1};
  pointer-events: ${p => p.disabled ? 'none' : 'auto'};

  &.primary {
    background: var(--accent);
    color: var(--text-on-accent);
    &:hover { background: var(--accent-hover); transform: translateX(4px); }
  }
  &.secondary {
    background: var(--surface-3);
    color: var(--text-heading);
    border: 1px solid var(--border-subtle);
    &:hover { background: var(--surface-glass-strong); transform: translateX(4px); }
  }
  &.accent {
    background: var(--accent-secondary-soft);
    color: var(--text-heading);
    border: 1px solid var(--border-default);
    &:hover { background: var(--accent-soft); transform: translateX(4px); }
  }
`;

export const StepBadge = styled.span`
  background: var(--surface-glass-strong);
  border-radius: 6px;
  padding: 0.15rem 0.45rem;
  font-size: 0.7rem;
  font-family: 'JetBrains Mono', monospace;
  opacity: 0.8;
`;

// ── Console ───────────────────────────────────────────────────────────────────
export const ConsoleBox = styled.div`
  background: var(--surface-overlay);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  padding: 1rem 1.25rem;
  height: 340px;
  overflow-y: auto;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 4px; opacity: 0.3; }
`;

export const ConsoleLine = styled.div`
  color: ${p =>
    p.type === "success" ? "var(--success)" :
    p.type === "error"   ? "var(--danger)" :
    p.type === "addr"    ? "var(--accent-secondary)" :
    "var(--text-muted)"};
  padding: 0.1rem 0;
  word-break: break-all;
  &::before { content: '> '; opacity: 0.5; }
`;

export const Cursor = styled.span`
  animation: ${pulse} 1s ease-in-out infinite;
  color: var(--accent);
`;

// ── Code panel ────────────────────────────────────────────────────────────────
export const CodePanel = styled(Panel)`
  margin-top: 1.5rem;
  animation: ${fadeInUp} 0.6s ease-out 0.3s both;
`;

export const CodeBlock = styled.pre`
  background: var(--surface-overlay);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  padding: 1.25rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
  line-height: 1.75;
  color: var(--text-secondary);
  overflow-x: auto;
  white-space: pre;
  /* Syntax highlight literals preserved for code readability */
  .kw  { color: #00b056; font-weight: 600; }
  .fn  { color: #dbfb8e; }
  .str { color: rgba(219,251,142,0.6); }
  .cm  { color: rgba(219,251,142,0.35); font-style: italic; }
`;

// ── Chips ─────────────────────────────────────────────────────────────────────
export const ChipsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  animation: ${fadeInUp} 0.6s ease-out 0.35s both;
`;

export const Chip = styled.div`
  background: var(--accent-soft);
  border: 1px solid var(--border-default);
  border-radius: 999px;
  padding: 0.4rem 1rem;
  font-size: 0.78rem;
  font-family: 'JetBrains Mono', monospace;
  color: var(--text-heading);
  letter-spacing: 0.03em;
  transition: all 0.2s;
  cursor: default;
  &:hover { background: var(--accent-soft); border-color: var(--border-strong); }
`;

// ── Workflow extras ───────────────────────────────────────────────────────────

export const SectionLabel = styled.p`
  font-size: 0.72rem;
  font-family: 'JetBrains Mono', monospace;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 1rem 0 0.5rem;
`;

export const NetworkSelector = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
`;

export const NetworkBtn = styled.button`
  padding: 0.4rem 0.9rem;
  border-radius: 8px;
  border: 1px solid ${p => p.active ? "var(--border-strong)" : "var(--border-subtle)"};
  background: ${p => p.active ? "var(--accent-soft)" : "transparent"};
  color: ${p => p.active ? "var(--text-heading)" : "var(--text-muted)"};
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: var(--border-strong);
    color: var(--text-heading);
    background: var(--accent-soft);
  }
`;

export const AccountPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  border-radius: 999px;
  padding: 0.4rem 1rem;
  width: fit-content;
`;

export const AccountDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.connected ? "var(--accent)" : "var(--border-subtle)"};
  box-shadow: ${p => p.connected ? "0 0 6px var(--accent)" : "none"};
  flex-shrink: 0;
`;

export const AccountText = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
  color: ${p => p.connected ? "var(--text-heading)" : "var(--text-muted)"};
`;

export const ConfigCard = styled.div`
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const ConfigRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
`;

export const ConfigLabel = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.7rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  min-width: 120px;
  flex-shrink: 0;
`;

export const ConfigValue = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.35rem;
  word-break: break-all;
`;

export const LockIcon = styled.span`
  font-size: 0.65rem;
  opacity: 0.5;
`;
